import React from "react"
import {
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
} from "react-share"
import styled from "styled-components"
import Image from "styleguide/components/Image"
import {
  IconFacebookShare,
  IconLinkedinShare,
  IconTwitterShare,
} from "styleguide/icons"
import { fontFamily } from "styleguide/styles/fonts"
import media from "styleguide/styles/media-queries"
import TeamMembers from "../../../content/About/TeamMembers"

interface Props {
  author: string
  link: string
}

const Wrapper = styled.div`
  margin-top: 80px;
  border-radius: 8px;
  border: 1px solid #d9e1e3;
  padding: 20px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${media.desktop`
    flex-direction: column;
    align-items: flex-start;
  `}
`

const Author = styled.div`
  display: flex;
  align-items: center;
  padding-right: 16px;

  img {
    width: 64px;
    border-radius: 32px;
    margin-right: 16px;
  }
`

const AuthorName = styled.div`
  font-size: 16px;
  font-family: ${fontFamily("heading")};
  line-height: 24px;
  font-weight: 600;
`

const AuthorPosition = styled.div`
  font-size: 13px;
  line-height: 16px;
  color: #88969a;
`

const Share = styled.div`
  display: flex;
  flex: 1 0 auto;
  align-items: center;
  justify-content: flex-end;
  margin-top: 0;

  ${media.desktop`
    margin-top: 24px;
    align-items: flex-start;
    flex-direction: column;
    width: 100%;
  `}
`

const ShareLabel = styled(AuthorName)`
  margin-right: 16px;

  ${media.desktop`
    margin-bottom: 16px;
  `}
`

const ShareIcons = styled.div`
  display: flex;

  & > * {
    margin-right: 8px;

    &:last-child {
      margin-right: 0;
    }

    ${media.desktop`
      flex: 1 0 auto;
    `}
  }

  ${media.desktop`
    width: 100%;
  `}
`

const IconShare = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 4px;
  background-color: #eef4f5;

  ${media.desktop`
    width: 100%;
  `}
`

const IconFacebookShareWrapper = styled(IconFacebookShare)`
  width: 9px;
  height: 20px;
`

const IconLinkedinShareWrapper = styled(IconLinkedinShare)`
  width: 18px;
  height: 17px;
`

const IconTwitterShareWrapper = styled(IconTwitterShare)`
  width: 20px;
  height: 16px;
`

const ImageWrapper = styled(Image)`
  width: 64px;
  border-radius: 32px;
  margin-right: 16px;
`

const ArticleAuthor = ({ author, link }: Props) => {
  const teamMember = TeamMembers.find(item => item.name === author)
  if (!teamMember) {
    throw new Error("Author not found!")
  }

  return (
    <Wrapper>
      {teamMember && (
        <Author>
          <ImageWrapper
            alt={`${teamMember.name} - ${teamMember.designation}`}
            filename={teamMember.imageUrl}
          />
          <div>
            <AuthorName>{teamMember.name}</AuthorName>
            {teamMember.active && (
              <AuthorPosition>
                {teamMember.designation} at ShakaCode
              </AuthorPosition>
            )}
          </div>
        </Author>
      )}
      <Share>
        <ShareLabel>Share this article</ShareLabel>
        <ShareIcons>
          <FacebookShareButton url={link}>
            <IconShare>
              <IconFacebookShareWrapper />
            </IconShare>
          </FacebookShareButton>

          <LinkedinShareButton url={link}>
            <IconShare>
              <IconLinkedinShareWrapper />
            </IconShare>
          </LinkedinShareButton>

          <TwitterShareButton url={link}>
            <IconShare>
              <IconTwitterShareWrapper />
            </IconShare>
          </TwitterShareButton>
        </ShareIcons>
      </Share>
    </Wrapper>
  )
}

export default ArticleAuthor
